/**
 * Which FORM a product's industry uses — and what that form needs.
 *
 * The rule (Frank, 2026-09-05): the product's industry decides the form, the
 * card, the receipt line and the filters; the brand's kind decides only which
 * pages exist. Picking "Food & Beverage" in the studio must give the menu
 * form, "Accommodation" the room form, "Pharmacy" a plain form with
 * prescription details — not a garment form with food categories.
 *
 * A registry industry row (convex `industries`) may carry `formTemplate`;
 * when it does not, the slug decides here, and unknown slugs fall back to
 * the attribute template they already ride (fashion → fashion, else specs).
 */

import type { Capability } from "./businessKinds";
import { EVENTS_INDUSTRY_SLUG } from "./events";

export type FormTemplate = "fashion" | "specs" | "plain" | "menu" | "room" | "vehicle" | "property";

export const FORM_TEMPLATE_KEYS: readonly FormTemplate[] = ["fashion", "specs", "plain", "menu", "room", "vehicle", "property"];

export const FORM_TEMPLATES: Record<FormTemplate, { label: string; blurb: string }> = {
  fashion: { label: "Fashion", blurb: "Colours, sizes, designs; photos on a mannequin or model; AI model generation." },
  specs: { label: "Specs", blurb: "Electronics: a spec axis (storage, size) instead of sizes; plain product photos." },
  plain: { label: "Plain product", blurb: "One item, one photo, one stock count — no colours or sizes. Pharmacy adds prescription details; hardware adds the unit of measure." },
  menu: { label: "Menu item", blurb: "Food or a drink: portions, dietary tags, spice, prep time, kitchen or bar." },
  room: { label: "Room type", blurb: "A room a hotel sells: rates, capacity, amenities, room numbers." },
  vehicle: { label: "Vehicle", blurb: "One physical car: make, model, year, chassis, plate, mileage, what it cost you, and whether it is for sale, for hire or both." },
  property: { label: "Property listing", blurb: "A house, flat, shop, plot or building for rent or for sale: beds, baths, size, tenure, where it is, and the price." },
};

/** Slug → template for the industries the platform ships with. */
export const DEFAULT_FORM_TEMPLATE_BY_SLUG: Readonly<Record<string, FormTemplate>> = {
  fashion: "fashion",
  electronics: "specs",
  "food-beverage": "menu",
  accommodation: "room",
  pharmacy: "plain",
  agrovet: "plain",
  hardware: "plain",
  motors: "vehicle",
  property: "property",
  // Flowers and décor ride the fashion variant engine (colours × sizes).
  events: "fashion",
  // Kits, boots, balls and weights: colours × sizes too (sizes per shelf).
  "sports-fitness": "fashion",
  // Tyres, batteries, oils and parts: one spec per shelf (tyre size, Ah,
  // litres), like electronics — so Electronics → Automotive products can move.
  automotive: "specs",
};

export function isFormTemplate(value: unknown): value is FormTemplate {
  return typeof value === "string" && (FORM_TEMPLATE_KEYS as readonly string[]).includes(value);
}

/**
 * The template for a product-industry slug. The registry row wins when it
 * names one; the shipped defaults come next; anything else rides its
 * attribute template (a platform-admin "gym" industry on specs stays specs).
 */
export function formTemplateFor(
  slug: string | null | undefined,
  row?: { formTemplate?: string | null; attributeTemplate?: string | null } | null,
): FormTemplate {
  if (row && isFormTemplate(row.formTemplate)) return row.formTemplate;
  if (slug && DEFAULT_FORM_TEMPLATE_BY_SLUG[slug]) return DEFAULT_FORM_TEMPLATE_BY_SLUG[slug];
  return row?.attributeTemplate === "fashion" ? "fashion" : "specs";
}

/**
 * Can a saved product move from one industry to another? Only when nothing
 * about its form changes: the same template and the same extra section, so
 * no stock, sizes or details are left behind (Fashion ↔ Events & Decor,
 * Pharmacy ↔ Agrovet). Anything else is a new product (Petal, 1 Oct 2026).
 * `rows` = the registry rows, when known (a row may name its own template).
 * `colours` = how many colours the product has, when known.
 */
export function canMoveProductIndustry(
  from: string | null | undefined,
  to: string | null | undefined,
  rows?: readonly { slug: string; formTemplate?: string | null; attributeTemplate?: string | null }[] | null,
  colours?: number | null,
): boolean {
  if (!from || !to) return false;
  if (from === to) return true;
  const rowOf = (slug: string) => rows?.find((r) => r.slug === slug) ?? null;
  const a = formTemplateFor(from, rowOf(from));
  const b = formTemplateFor(to, rowOf(to));
  if (a !== b || attributeTemplateFor(a) !== attributeTemplateFor(b)) return false;
  if (industryDetailsFor(from) !== industryDetailsFor(to)) return false;
  // Only Events & Decor sells a product with no colour (its stock sits on
  // variant-default-*); every other colour × size form needs one, so a
  // colourless product stays where it is.
  if (a === "fashion" && colours === 0 && (from === EVENTS_INDUSTRY_SLUG || to === EVENTS_INDUSTRY_SLUG)) return false;
  return true;
}

/** The attribute bucket a template writes (`fashionAttributes` vs `electronicsAttributes`). */
export function attributeTemplateFor(template: FormTemplate): "fashion" | "electronics" {
  return template === "fashion" ? "fashion" : "electronics";
}

export type IndustryDetails = "pharmacy" | "measure";

/** The industry-specific section a product of this industry carries on the plain form. */
export function industryDetailsFor(slug: string | null | undefined): IndustryDetails | null {
  if (slug === "pharmacy" || slug === "agrovet") return "pharmacy";
  if (slug === "hardware") return "measure";
  return null;
}

/**
 * The brand capability a product of this industry needs beyond the till —
 * the Kitchen for food, the Front Desk for rooms, the Expiry board for
 * medicines, Quotations for building materials. null = the till is enough.
 */
export function industryCapability(slug: string | null | undefined): Capability | null {
  switch (slug) {
    case "food-beverage":
      return "food";
    case "accommodation":
      return "rooms";
    case "pharmacy":
    case "agrovet":
      return "pharmacy";
    case "hardware":
      return "hardware";
    case "motors":
      return "motors";
    case "property":
      return "rentals";
    case "events":
      return "events";
    default:
      return null;
  }
}

/** What a shop reads on the till's industry pills and the add-on prompt. */
export const INDUSTRY_SHORT_LABELS: Readonly<Record<string, string>> = {
  fashion: "Fashion",
  electronics: "Electronics",
  "food-beverage": "Menu",
  accommodation: "Rooms",
  pharmacy: "Pharmacy",
  agrovet: "Agrovet",
  hardware: "Building materials",
  motors: "Cars",
  property: "Property",
  events: "Events & décor",
  "sports-fitness": "Sports & fitness",
  automotive: "Auto parts",
};

export function industryShortLabel(slug: string | null | undefined, fallback?: string): string {
  if (slug && INDUSTRY_SHORT_LABELS[slug]) return INDUSTRY_SHORT_LABELS[slug];
  return fallback || (slug ? slug.replace(/-/g, " ") : "Other");
}
